export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  imageUrl: string;
  author: string;
  publishDate: string;
  readTime: string;
  tags: string[];
}

export const articles: Article[] = [
  {
    slug: 'bladder-basics-structure-function',
    title: '膀胱基础知识：结构与功能详解',
    excerpt: '深入了解膀胱的解剖结构、生理功能以及它在泌尿系统中的核心作用，为膀胱健康管理打下坚实的理论基础。',
    content: `<p>膀胱是人体泌尿系统中一个至关重要的器官，它像一个富有弹性的"储水袋"，负责暂时储存由肾脏不断产生的尿液，并在适当的时机将其排出体外。了解膀胱的基本结构与工作原理，是进行科学膀胱健康管理的第一步。</p>

<h2>膀胱的解剖结构</h2>
<p>膀胱位于盆腔前部，是一个中空、肌肉发达的囊状器官。它的形状和大小会随着尿液的充盈程度而变化——空虚时呈锥体形，充盈时则呈卵圆形。膀胱壁由四层组织构成：</p>
<ul>
<li><strong>黏膜层：</strong>最内层，由移行上皮细胞构成，具有极强的延展性，能够适应膀胱容积的巨大变化。</li>
<li><strong>黏膜下层：</strong>富含血管和弹性纤维，提供营养和支撑。</li>
<li><strong>肌层（逼尿肌）：</strong>由平滑肌纤维编织而成，在排尿时协调收缩，将尿液排出。</li>
<li><strong>外膜层：</strong>最外层的保护性结缔组织。</li>
</ul>

<h2>膀胱的核心功能</h2>
<p>膀胱的工作机制精妙而高效，主要包含两个阶段：</p>
<h3>储存期</h3>
<p>在尿液储存阶段，逼尿肌保持松弛状态，尿道括约肌则紧张收缩，确保尿液不会泄漏。正常成年人膀胱可以舒适地容纳约300-500毫升尿液。当尿液量达到约150-200毫升时，膀胱壁上的感受器开始向大脑发送"需要排尿"的信号。</p>
<h3>排空期</h3>
<p>当条件适宜时，大脑发出排尿指令：逼尿肌协调收缩产生压力，同时尿道括约肌放松，尿液顺畅排出。整个过程需要神经系统、肌肉系统的精密配合。</p>

<h2>膀胱健康的警示信号</h2>
<p>关注以下信号有助于及早发现膀胱健康问题：</p>
<ul>
<li><strong>尿频：</strong>白天排尿超过8次，或夜间起床排尿超过1次</li>
<li><strong>尿急：</strong>突然产生强烈的排尿冲动，难以延迟</li>
<li><strong>排尿困难：</strong>排尿时需要用力，或尿流细弱、断断续续</li>
<li><strong>排尿疼痛：</strong>排尿时有灼烧感或疼痛</li>
<li><strong>尿失禁：</strong>不自主的尿液泄漏</li>
</ul>
<p>如出现以上症状，建议及时就医咨询专业医生。</p>

<h2>维护膀胱健康的日常习惯</h2>
<p>良好的生活习惯是膀胱健康的根本保障：保持充足但不过量的饮水（每日约1.5-2升），避免长时间憋尿，养成定时排尿的习惯，注意个人卫生，适度进行盆底肌锻炼。这些看似简单的日常行为，长期坚持将对膀胱健康产生深远的积极影响。</p>`,
    category: 'bladder-health',
    imageUrl: 'https://readdy.ai/api/search-image?query=Abstract%20anatomical%20illustration%20of%20human%20urinary%20system%20with%20soft%20warm%20golden%20and%20cream%20tones%2C%20minimalist%20medical%20art%20style%2C%20gentle%20watercolor%20texture%2C%20clean%20educational%20aesthetic%2C%20healing%20and%20calm%20atmosphere%2C%20no%20text%20or%20labels&width=800&height=500&seq=article-bladder-basics&orientation=landscape',
    author: '李医生',
    publishDate: '2026-07-15',
    readTime: '8分钟',
    tags: ['膀胱结构', '泌尿系统', '健康知识'],
  },
  {
    slug: 'pelvic-floor-exercise-guide',
    title: '盆底肌训练完全指南：从入门到精通',
    excerpt: '盆底肌是支撑膀胱的关键肌群，本文详细介绍盆底肌的正确识别方法、分阶段训练方案和常见误区纠正。',
    content: `<p>盆底肌群被称为人体的"隐形吊床"，它承托着膀胱、子宫和直肠等重要器官。强健的盆底肌是良好膀胱控制能力的基石，而科学系统的训练则是维持盆底肌健康的关键。</p>

<h2>认识你的盆底肌</h2>
<p>盆底肌位于骨盆底部，是一组从耻骨延伸到尾骨的肌肉和结缔组织。它们像一个富有弹性的"吊床"，在支撑盆腔器官的同时，还参与控制排尿、排便等生理功能。</p>

<h3>如何找到盆底肌</h3>
<p>正确感知盆底肌是有效训练的前提。以下几种方法可以帮助你定位盆底肌：</p>
<ul>
<li><strong>中断排尿法：</strong>在排尿过程中尝试中断尿流，此时收缩的肌肉就是盆底肌。注意：此方法仅用于定位，不应作为常规训练方式。</li>
<li><strong>想象法：</strong>想象你在努力阻止排气或憋住小便，此时收紧的肌肉群就是盆底肌。</li>
<li><strong>手指感知法：</strong>将清洁的手指放入阴道或直肠，收缩盆底肌时能感受到手指被轻轻包裹和上提。</li>
</ul>

<h2>分阶段训练方案</h2>

<h3>第一阶段：基础感知训练（第1-2周）</h3>
<p>目标：建立正确的肌肉感知和控制能力。</p>
<ul>
<li>平躺，双膝弯曲，双脚平放地面</li>
<li>缓慢收缩盆底肌，感受肌肉向内、向上的提升感</li>
<li>保持收缩3-5秒，然后完全放松5秒</li>
<li>每组重复10次，每天做3组</li>
<li>重点：确保腹部、臀部和大腿肌肉保持放松</li>
</ul>

<h3>第二阶段：耐力增强训练（第3-6周）</h3>
<p>目标：延长肌肉持续收缩的时间，增强耐力。</p>
<ul>
<li>在坐姿或站姿下进行训练</li>
<li>逐渐将每次收缩保持时间延长到8-10秒</li>
<li>放松时间与收缩时间保持一致</li>
<li>每组重复10-15次，每天做3-4组</li>
</ul>

<h3>第三阶段：功能性训练（第7周起）</h3>
<p>目标：在日常活动中维持盆底肌的协调工作。</p>
<ul>
<li>在咳嗽、打喷嚏、提重物前预先收缩盆底肌（称为"预收缩"技巧）</li>
<li>在走路、上下楼梯时练习盆底肌的轻微持续收缩</li>
<li>结合深呼吸进行训练：吸气时放松，呼气时收缩</li>
</ul>

<h2>常见训练误区</h2>
<ul>
<li><strong>用错肌肉：</strong>收缩臀大肌或腹肌代替盆底肌，不仅无效还可能增加腹压</li>
<li><strong>屏住呼吸：</strong>训练时应保持自然呼吸，屏气会增加腹内压力</li>
<li><strong>半途而废：</strong>盆底肌训练需要至少坚持6-8周才能看到明显效果</li>
<li><strong>过度训练：</strong>肌肉需要休息和恢复，过度训练可能导致肌肉疲劳</li>
</ul>

<p>坚持科学的盆底肌训练，通常6-8周后可以感受到膀胱控制能力的明显改善。如果在训练过程中出现不适，请及时咨询专业康复医师。</p>`,
    category: 'training-methods',
    imageUrl: 'https://readdy.ai/api/search-image?query=Soft%20abstract%20wellness%20illustration%20of%20body%20core%20and%20pelvic%20health%2C%20warm%20amber%20and%20cream%20tones%2C%20minimalist%20style%2C%20gentle%20flowing%20lines%2C%20calm%20healing%20atmosphere%2C%20no%20text%20or%20labels&width=800&height=500&seq=article-pelvic-floor&orientation=landscape',
    author: '王康复师',
    publishDate: '2026-07-20',
    readTime: '10分钟',
    tags: ['盆底肌', '训练方法', '康复锻炼'],
  },
  {
    slug: 'bladder-irritant-foods',
    title: '膀胱刺激性食物清单：哪些该吃哪些该避',
    excerpt: '饮食对膀胱健康的影响比你想的更大。本文整理了一份详细的膀胱友好与刺激性食物对照清单。',
    content: `<p>你可能没有意识到，日常饮食中的许多食物和饮品都会直接影响膀胱的功能和舒适度。了解哪些食物是膀胱的"朋友"，哪些是"敌人"，可以帮助你通过饮食调整显著改善膀胱健康状况。</p>

<h2>常见膀胱刺激物</h2>
<p>以下食物和饮品被证实可能刺激膀胱，加重尿频、尿急等症状：</p>

<h3>饮品类的刺激物</h3>
<ul>
<li><strong>咖啡和浓茶：</strong>咖啡因是强效利尿剂，同时刺激膀胱平滑肌</li>
<li><strong>碳酸饮料：</strong>二氧化碳气泡和人工甜味剂都可能刺激膀胱</li>
<li><strong>酒精类饮品：</strong>酒精具有利尿和刺激膀胱的双重作用</li>
<li><strong>高糖果汁：</strong>过高的糖分和酸性物质增加膀胱刺激</li>
<li><strong>能量饮料：</strong>高浓度咖啡因和其他兴奋剂对膀胱极不友好</li>
</ul>

<h3>食物类的刺激物</h3>
<ul>
<li><strong>辛辣食物：</strong>辣椒、花椒等辛辣调味料可直接刺激膀胱黏膜</li>
<li><strong>柑橘类水果：</strong>橙子、柠檬、柚子等高酸性水果</li>
<li><strong>番茄及番茄制品：</strong>酸性较强，部分人会出现膀胱不适</li>
<li><strong>巧克力：</strong>含有咖啡因和可可碱，可能刺激膀胱</li>
<li><strong>人工甜味剂：</strong>阿斯巴甜、糖精等可能加重膀胱刺激</li>
<li><strong>洋葱和大蒜：</strong>部分人群对此类食物敏感</li>
</ul>

<h2>膀胱友好型食物推荐</h2>

<h3>优质蛋白质</h3>
<ul>
<li>鸡胸肉、火鸡肉（白肉低脂）</li>
<li>新鲜鱼类（富含Omega-3脂肪酸）</li>
<li>鸡蛋</li>
<li>豆腐及其他豆制品（温和且营养丰富）</li>
</ul>

<h3>膀胱友好蔬菜</h3>
<ul>
<li>西兰花和花椰菜</li>
<li>胡萝卜</li>
<li>南瓜（富含膳食纤维且温和）</li>
<li>黄瓜（含水量高，清淡不刺激）</li>
<li>菠菜（富含镁，有助肌肉放松）</li>
<li>芹菜</li>
</ul>

<h3>适宜水果</h3>
<ul>
<li>香蕉（富含钾，pH值温和）</li>
<li>梨（低酸性，含丰富纤维）</li>
<li>蓝莓（抗氧化，低酸性）</li>
<li>西瓜（含水量高，利尿但温和）</li>
<li>苹果（中等酸性，多数人可以耐受）</li>
</ul>

<h2>膀胱健康饮水指南</h2>
<p>饮水是膀胱健康管理的关键环节。建议每日饮用1.5-2升水，但应注意：</p>
<ul>
<li>分散饮水，避免一次性大量饮水</li>
<li>睡前2小时减少饮水，避免夜尿</li>
<li>外出前规划好饮水时间，确保有如厕条件</li>
<li>首选白开水，其次是淡雅的草本茶（如菊花茶）</li>
</ul>

<h2>饮食排除法：找到你的"敏感食物"</h2>
<p>每个人的体质不同，对食物的反应也各不相同。建议采用"饮食日记+逐步排除"的方法：</p>
<ol>
<li>记录一周的饮食和排尿情况</li>
<li>识别可能的关联模式</li>
<li>逐一排除可疑食物2-3周，观察症状变化</li>
<li>逐步重新引入，确认敏感食物</li>
</ol>
<p>通过这种系统的方法，你可以精确找到自己的膀胱"触发食物"，从而制定个性化的饮食方案。</p>`,
    category: 'diet-nutrition',
    imageUrl: 'https://readdy.ai/api/search-image?query=Warm%20toned%20still%20life%20of%20healthy%20fresh%20fruits%20and%20vegetables%20on%20cream%20background%2C%20soft%20natural%20lighting%2C%20minimalist%20food%20photography%2C%20golden%20and%20warm%20amber%20accents%2C%20clean%20wellness%20aesthetic&width=800&height=500&seq=article-diet-foods&orientation=landscape',
    author: '张营养师',
    publishDate: '2026-07-25',
    readTime: '9分钟',
    tags: ['饮食调理', '膀胱刺激', '健康饮食'],
  },
  {
    slug: 'timed-voiding-technique',
    title: '定时排尿法：重建膀胱节律的科学方案',
    excerpt: '定时排尿是膀胱再训练的核心技术之一。本文详细讲解如何制定个性化排尿计划，逐步延长排尿间隔。',
    content: `<p>定时排尿法（Timed Voiding）是一种科学有效的膀胱再训练技术。通过建立规律的排尿时间表，可以帮助膀胱恢复正常的储存和排空节律，显著改善尿频和尿急症状。</p>

<h2>什么是定时排尿法</h2>
<p>定时排尿法的核心理念是按照固定的时间间隔主动排尿，而不是等到产生强烈的尿意时才去洗手间。这种方法打破了"尿急-频繁排尿-膀胱容量减小-更加尿急"的恶性循环，帮助膀胱逐步恢复到正常的容量和功能状态。</p>

<h2>适用人群</h2>
<ul>
<li>功能性尿频患者</li>
<li>膀胱过度活动症（OAB）患者</li>
<li>习惯性尿频者</li>
<li>希望改善膀胱控制能力的健康人群</li>
</ul>

<h2>实施步骤</h2>

<h3>第一步：建立基线记录（3-5天）</h3>
<p>在开始训练前，先完成一份详细的"排尿日记"：</p>
<ul>
<li>记录每次排尿的具体时间</li>
<li>记录每次排尿的紧急程度（1-5分评分）</li>
<li>记录每日饮水量和饮水时间</li>
<li>记录任何漏尿事件及其发生时的情境</li>
</ul>

<h3>第二步：设定初始排尿间隔</h3>
<p>根据排尿日记的数据，计算你目前的平均排尿间隔时间。初始训练间隔设为比当前平均间隔稍短的时间，确保在这个间隔内你基本不会出现尿急或漏尿。</p>
<p>例如：如果你的平均排尿间隔是1.5小时，初始训练间隔可设为1小时或1小时15分钟。</p>

<h3>第三步：严格执行定时排尿</h3>
<ul>
<li>使用闹钟或手机提醒，严格按时排尿</li>
<li>即使没有尿意，也要在规定时间去洗手间</li>
<li>如果在两次预定排尿之间产生尿急感，使用延迟技巧（见下文）</li>
<li>坚持初始间隔1-2周，直到感觉舒适</li>
</ul>

<h3>第四步：逐步延长间隔</h3>
<ul>
<li>当你能在当前间隔内无压力地排尿时，延长15-30分钟</li>
<li>每次延长后至少保持2-3天再考虑下一次延长</li>
<li>目标是将排尿间隔延长到2.5-4小时</li>
<li>夜间排尿的理想目标是0-1次</li>
</ul>

<h2>应对尿急的延迟技巧</h2>
<p>在训练过程中，当尿急感来袭时，试试这些方法来延迟排尿：</p>
<ul>
<li><strong>深呼吸法：</strong>缓慢深长的腹式呼吸，帮助放松身体和膀胱</li>
<li><strong>盆底肌快速收缩：</strong>快速有力地收缩盆底肌5-10次，可暂时抑制尿急感</li>
<li><strong>注意力转移：</strong>进行需要集中注意力的活动，如倒数计算、拼图游戏</li>
<li><strong>坐姿调整：</strong>坐在硬质椅子边缘，或交叉双腿，对会阴部施加轻微压力</li>
<li><strong>按压法：</strong>轻轻按压会阴部或脚底涌泉穴</li>
</ul>

<h2>训练中的注意事项</h2>
<ul>
<li><strong>保持充足饮水：</strong>不要为了减少排尿而限制饮水，这会适得其反</li>
<li><strong>避免过度憋尿：</strong>如果延迟至疼痛或极度不适，应适当缩短间隔</li>
<li><strong>循序渐进：</strong>训练效果不会立竿见影，通常需要4-8周才能看到明显改善</li>
<li><strong>个体化调整：</strong>根据自身情况灵活调整方案，不必苛求标准</li>
<li><strong>坚持记录：</strong>持续记录排尿日记，追踪进步情况</li>
</ul>

<p>定时排尿法是膀胱再训练中最安全、最有效的方法之一。只要坚持按计划执行，绝大多数人在2-3个月内都能看到显著改善。</p>`,
    category: 'training-methods',
    imageUrl: 'https://readdy.ai/api/search-image?query=Aesthetic%20minimalist%20clock%20and%20calendar%20flatlay%20with%20warm%20cream%20and%20golden%20tones%2C%20soft%20shadows%2C%20clean%20wellness%20lifestyle%20concept%2C%20gentle%20morning%20light%2C%20calm%20organized%20composition&width=800&height=500&seq=article-timed-voiding&orientation=landscape',
    author: '李医生',
    publishDate: '2026-07-28',
    readTime: '9分钟',
    tags: ['定时排尿', '膀胱训练', '行为疗法'],
  },
  {
    slug: 'water-intake-bladder-health',
    title: '科学饮水与膀胱健康：喝对水比喝多水更重要',
    excerpt: '很多人误以为"多喝水"就能保护膀胱健康，但科学饮水的关键在于喝对时间、喝对方式、喝对量。',
    content: `<p>"多喝水"是我们最常听到的健康建议之一，但对于膀胱健康来说，喝水的科学性远不止"多"那么简单。喝多少、什么时候喝、怎么喝——这些细节都直接影响着膀胱的健康状态和排尿体验。</p>

<h2>每日饮水量：因人而异</h2>
<p>关于每日饮水量，并没有一刀切的标准。影响个人饮水需求的因素包括：</p>
<ul>
<li>体重和体表面积</li>
<li>活动量和运动强度</li>
<li>气候环境（温度、湿度）</li>
<li>饮食结构（汤类、水果等含水量）</li>
<li>个体代谢速率</li>
<li>健康状况和用药情况</li>
</ul>
<p>一般来说，大多数成年人每日从饮水和食物中获取的总水量建议在2-3升之间，其中直接饮水约1.5-2升。一个简单的判断方法是观察尿液颜色——淡稻草色或浅黄色通常表示水分充足。</p>

<h2>饮水的时间策略</h2>

<h3>推荐饮水时间</h3>
<ul>
<li><strong>早晨起床后：</strong>空腹饮用200-300毫升温水，补充夜间水分流失</li>
<li><strong>上午工作间隙：</strong>均匀分散饮水，每次150-200毫升</li>
<li><strong>午后：</strong>保持适量饮水，维持代谢和身体功能</li>
<li><strong>运动前后：</strong>运动前30分钟和运动后适量补水</li>
</ul>

<h3>需要注意的饮水时间</h3>
<ul>
<li><strong>睡前2小时：</strong>逐步减少饮水，避免夜尿干扰睡眠</li>
<li><strong>外出前：</strong>提前规划，确保目的地有如厕条件</li>
<li><strong>重要会议/活动前：</strong>提前1小时适量饮水后排空膀胱</li>
</ul>

<h2>正确的饮水方式</h2>
<ul>
<li><strong>小口慢饮：</strong>每次饮水不要大口猛灌，小口慢饮更利于吸收</li>
<li><strong>分散饮水：</strong>将全天饮水量均匀分布，避免集中大量饮水</li>
<li><strong>温度适宜：</strong>温水（25-40℃）对膀胱最为温和</li>
<li><strong>避免冰水：</strong>冰水可能刺激膀胱，诱发尿急</li>
</ul>

<h2>不同饮品的膀胱影响</h2>
<table>
<tr><th>饮品类型</th><th>对膀胱的影响</th><th>建议</th></tr>
<tr><td>白开水</td><td>最佳饮品，温和无刺激</td><td>首选，每日主要饮品</td></tr>
<tr><td>淡菊花茶</td><td>温和利尿，不刺激膀胱</td><td>可适量饮用</td></tr>
<tr><td>大麦茶</td><td>无咖啡因，温和不刺激</td><td>适宜替代咖啡</td></tr>
<tr><td>咖啡</td><td>利尿刺激，可能诱发尿急</td><td>每日不超过1小杯</td></tr>
<tr><td>绿茶</td><td>含咖啡因，中等刺激</td><td>避免空腹饮用</td></tr>
<tr><td>碳酸饮料</td><td>高刺激性</td><td>尽量避免</td></tr>
</table>

<h2>常见饮水误区</h2>
<ul>
<li><strong>误区一：</strong>"渴了才喝水"——感到口渴时身体已经处于轻度缺水状态</li>
<li><strong>误区二：</strong>"一次喝个够"——集中大量饮水会急剧增加膀胱负担</li>
<li><strong>误区三：</strong>"少喝水能减少上厕所"——浓缩的尿液反而更刺激膀胱黏膜</li>
<li><strong>误区四：</strong>"运动饮料对健康好"——含糖和电解质的运动饮料可能刺激膀胱</li>
</ul>

<p>科学的饮水习惯是膀胱健康的重要保障。请记住：关键在于"规律、适量、分散"，而非简单地"多喝"。如果你有膀胱健康方面的困扰，从调整饮水习惯开始，往往能收获意想不到的改善效果。</p>`,
    category: 'daily-care',
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20glass%20of%20water%20with%20soft%20morning%20light%2C%20warm%20cream%20and%20golden%20background%2C%20minimalist%20wellness%20photography%2C%20gentle%20bokeh%20effect%2C%20clean%20and%20pure%20aesthetic%2C%20calm%20healing%20vibe&width=800&height=500&seq=article-water-health&orientation=landscape',
    author: '张营养师',
    publishDate: '2026-08-01',
    readTime: '8分钟',
    tags: ['科学饮水', '日常保健', '膀胱健康'],
  },
  {
    slug: 'nocturia-causes-solutions',
    title: '夜尿频繁怎么办？原因分析与综合解决方案',
    excerpt: '夜间频繁起夜严重影响睡眠质量和生活品质。本文系统性分析夜尿的多种原因，提供从生活习惯到医疗干预的全方位解决方案。',
    content: `<p>夜尿症（Nocturia）是指在夜间睡眠期间需要起床排尿一次或多次的情况。虽然偶尔的夜尿是正常的，但频繁的夜间排尿（每晚2次及以上）会严重影响睡眠质量，导致日间疲劳、注意力下降，长期还可能增加跌倒风险和心血管疾病风险。</p>

<h2>夜尿的常见原因分析</h2>

<h3>生活习惯因素</h3>
<ul>
<li><strong>睡前大量饮水：</strong>睡前进水过多是夜尿最常见的原因</li>
<li><strong>晚间摄入利尿饮品：</strong>咖啡、茶、酒精等会增加夜间尿量</li>
<li><strong>晚餐过咸：</strong>高盐饮食导致体内水分潴留，夜间排出</li>
<li><strong>不规律的作息：</strong>生物钟紊乱影响抗利尿激素的正常分泌</li>
</ul>

<h3>生理与病理因素</h3>
<ul>
<li><strong>年龄增长：</strong>膀胱容量减小，夜间尿液浓缩能力下降</li>
<li><strong>膀胱过度活动症：</strong>膀胱敏感度异常增高</li>
<li><strong>前列腺问题（男性）：</strong>前列腺增生压迫尿道</li>
<li><strong>盆底肌松弛（女性）：</strong>尤其是产后女性</li>
<li><strong>糖尿病：</strong>高血糖导致渗透性利尿</li>
<li><strong>睡眠呼吸暂停：</strong>呼吸暂停刺激心房利钠肽分泌</li>
<li><strong>心力衰竭：</strong>日间下肢水肿在夜间平卧时回流</li>
</ul>

<h2>综合改善方案</h2>

<h3>第一步：调整晚间生活习惯</h3>
<ul>
<li>睡前2-3小时停止饮水</li>
<li>晚餐清淡少盐，避免过饱</li>
<li>午后开始避免咖啡、浓茶、酒精</li>
<li>睡前务必排空膀胱</li>
<li>下午或傍晚适当抬高双腿，促进下肢液体回流</li>
</ul>

<h3>第二步：优化睡眠环境</h3>
<ul>
<li>保持卧室适宜温度（18-22℃）</li>
<li>使用遮光窗帘，营造黑暗环境</li>
<li>保持规律的就寝和起床时间</li>
<li>睡前进行放松活动（深呼吸、温和拉伸）</li>
</ul>

<h3>第三步：膀胱训练与盆底肌强化</h3>
<ul>
<li>坚持日间定时排尿训练，延长排尿间隔</li>
<li>每日进行盆底肌训练（凯格尔运动）</li>
<li>学习尿急应对技巧，减少不必要的夜间排尿</li>
</ul>

<h3>第四步：医疗咨询与干预</h3>
<p>如果上述生活方式调整2-4周后夜尿无明显改善，建议就医：</p>
<ul>
<li>泌尿科：评估膀胱功能和排尿系统结构</li>
<li>记录1周排尿日记，帮助医生准确诊断</li>
<li>根据具体病因，医生可能建议药物治疗或物理治疗</li>
</ul>

<h2>特殊人群的夜尿管理</h2>
<ul>
<li><strong>老年人：</strong>夜间起床注意防跌倒，床边备便器，安装夜灯</li>
<li><strong>孕妇：</strong>孕晚期夜尿属正常现象，注意侧卧睡姿减轻子宫压迫</li>
<li><strong>服药人群：</strong>咨询医生是否可以调整利尿剂等药物的服用时间至早晨</li>
</ul>

<p>夜尿问题虽然困扰，但绝大多数情况下是可以通过系统性的调整得到显著改善的。关键在于找出根本原因，采取有针对性的措施，并保持耐心和坚持。</p>`,
    category: 'daily-care',
    imageUrl: 'https://readdy.ai/api/search-image?query=Peaceful%20bedroom%20scene%20with%20soft%20moonlight%20through%20window%2C%20warm%20cream%20and%20soft%20golden%20tones%2C%20minimalist%20interior%2C%20calm%20and%20restful%20atmosphere%2C%20clean%20healing%20aesthetic%2C%20gentle%20dreamy%20vibe&width=800&height=500&seq=article-nocturia&orientation=landscape',
    author: '李医生',
    publishDate: '2026-08-02',
    readTime: '9分钟',
    tags: ['夜尿', '睡眠健康', '日常保健'],
  },
  {
    slug: 'urinary-health-myths',
    title: '泌尿健康十大常见误区：你可能一直信以为真',
    excerpt: '关于膀胱和泌尿健康，流传着许多看似有理的"常识"，但它们可能并不科学。本文一一揭开这些误区。',
    content: `<p>在泌尿健康领域，流传着许多广为接受的"常识"。然而，其中不少说法缺乏科学依据，有些甚至可能对你的膀胱健康造成负面影响。让我们一起揭开这些常见误区的真相。</p>

<h2>误区一：憋尿可以"锻炼"膀胱</h2>
<p><strong>真相：</strong>适度的膀胱训练（如定时排尿法）确实有助于改善膀胱功能，但长期刻意憋尿直到疼痛，会增加尿路感染风险，甚至可能导致尿液反流损伤肾脏。正确的做法是循序渐进地延长排尿间隔，而不是强行忍耐。</p>

<h2>误区二：少喝水能减少上厕所次数</h2>
<p><strong>真相：</strong>减少饮水确实会暂时减少尿量，但浓缩的尿液对膀胱黏膜的刺激性更强，反而可能加重尿急和不适。而且，长期饮水不足会增加尿路感染和结石的风险。</p>

<h2>误区三：蔓越莓汁能治所有尿路问题</h2>
<p><strong>真相：</strong>蔓越莓含有原花青素，可能有助于预防某些类型的尿路感染复发，但它不是"万能药"，对已经发生的感染没有治疗作用，也不适用于所有泌尿问题。</p>

<h2>误区四：尿频就是肾虚</h2>
<p><strong>真相：</strong>尿频的原因多种多样，包括膀胱过度活动、饮水习惯、心理因素、糖尿病等。简单归咎于"肾虚"可能导致忽视真正的病因。</p>

<h2>误区五：术后或产后漏尿是正常现象，只能忍受</h2>
<p><strong>真相：</strong>虽然产后和术后漏尿较为常见，但这不意味着只能被动忍受。盆底肌康复训练、物理治疗、生活方式调整等多种方法都可以有效改善漏尿问题。</p>

<h2>误区六：喝咖啡不影响泌尿健康</h2>
<p><strong>真相：</strong>咖啡因是已知的膀胱刺激物和利尿剂。对于膀胱敏感的人群，即使一杯咖啡也可能导致尿频和尿急。如果你有膀胱不适，暂时戒咖啡2周看看效果如何。</p>

<h2>误区七：排尿排得越干净越好</h2>
<p><strong>真相：</strong>正常排尿后膀胱内残留少量尿液（约50毫升以内）是正常现象。过分用力"排尽"并非必要，反而可能导致盆底肌过度紧张。</p>

<h2>误区八：只有老年人才有膀胱问题</h2>
<p><strong>真相：</strong>膀胱健康问题可以发生在任何年龄段。年轻人因不良生活习惯、工作压力、缺少锻炼等，同样可能出现膀胱功能问题。</p>

<h2>误区九：盆底肌训练只适合女性</h2>
<p><strong>真相：</strong>男性和女性都有盆底肌，男性同样可以通过盆底肌训练改善排尿控制能力，特别是有前列腺问题的男性。</p>

<h2>误区十：尿路问题自己买药就行，不需要看医生</h2>
<p><strong>真相：</strong>不同类型的泌尿问题需要不同的处理方式。自行用药可能延误正确治疗，甚至掩盖严重疾病的早期信号。出现以下情况请及时就医：排尿疼痛、肉眼血尿、持续超过1周的排尿异常。</p>

<p>科学的认知是健康管理的第一步。希望这篇文章能帮助你摒弃不科学的"常识"，用正确的方式呵护你的泌尿健康。</p>`,
    category: 'bladder-health',
    imageUrl: 'https://readdy.ai/api/search-image?query=Abstract%20concept%20of%20question%20marks%20and%20light%20bulb%20moments%2C%20warm%20amber%20and%20cream%20gradient%20background%2C%20minimalist%20educational%20illustration%2C%20soft%20textures%2C%20curiosity%20and%20knowledge%20theme%2C%20clean%20modern%20aesthetic&width=800&height=500&seq=article-myths&orientation=landscape',
    author: '王康复师',
    publishDate: '2026-08-03',
    readTime: '8分钟',
    tags: ['健康误区', '泌尿知识', '科普辟谣'],
  },
  {
    slug: 'stress-incontinence-recovery',
    title: '压力性尿失禁的康复之路：非手术疗法全攻略',
    excerpt: '咳嗽、打喷嚏、跳跃时漏尿是压力性尿失禁的典型表现。本文介绍从盆底肌训练到生活调整的完整康复方案。',
    content: `<p>压力性尿失禁（Stress Urinary Incontinence, SUI）是最常见的尿失禁类型，表现为在咳嗽、打喷嚏、大笑、跳跃或提重物等腹压增高的情况下，出现不自主的尿液泄漏。虽然令人尴尬和困扰，但好消息是——多数压力性尿失禁通过非手术疗法都能得到显著改善。</p>

<h2>为什么会发生压力性尿失禁</h2>
<p>压力性尿失禁的根本原因在于盆底支撑结构（肌肉、筋膜、韧带）的功能减弱，导致尿道括约肌在腹压增高时无法有效关闭尿道。常见诱因包括：</p>
<ul>
<li>妊娠和分娩（尤其是顺产）对盆底的损伤</li>
<li>年龄增长导致的盆底肌自然退化</li>
<li>慢性咳嗽、长期便秘导致的持续腹压增高</li>
<li>肥胖增加盆底的持续负荷</li>
<li>某些高强度运动（如跑步、跳跃）的长期影响</li>
<li>盆腔手术史</li>
</ul>

<h2>非手术治疗方案</h2>

<h3>一、盆底肌训练（凯格尔运动）——首选基础疗法</h3>
<p>盆底肌训练是压力性尿失禁治疗的"金标准"，科学证据最充分：</p>
<ul>
<li>每日3组，每组10-15次收缩</li>
<li>每次收缩保持8-10秒，逐步增加</li>
<li>坚持至少3个月才能看到显著效果</li>
<li>研究显示，规范训练可使50%-75%的患者漏尿量减少50%以上</li>
</ul>

<h3>二、生活方式综合调整</h3>
<ul>
<li><strong>体重管理：</strong>减重5%-10%可显著降低腹压对盆底的负荷</li>
<li><strong>便秘管理：</strong>增加膳食纤维摄入，避免排便时的过度用力</li>
<li><strong>咳嗽管理：</strong>慢性咳嗽患者可在咳嗽前预先收缩盆底肌</li>
<li><strong>运动选择：</strong>避免高强度跳跃类运动，选择游泳、快走等低冲击运动</li>
<li><strong>戒烟：</strong>吸烟与慢性咳嗽及盆底组织退化均相关</li>
</ul>

<h3>三、生物反馈治疗</h3>
<p>生物反馈设备可以帮助你直观地"看到"盆底肌的收缩情况，确保你在正确使用目标肌肉。对于难以感知盆底肌收缩的人群特别有帮助。</p>

<h3>四、阴道哑铃训练</h3>
<p>使用不同重量的阴道锥体进行训练，通过本体感觉反馈增强盆底肌力量和协调性。从较轻的重量开始，逐步递增。</p>

<h3>五、电刺激治疗</h3>
<p>通过低频电流刺激盆底肌被动收缩，适用于无法自主收缩盆底肌或肌肉力量极弱的情况。通常在专业康复师的指导下进行。</p>

<h2>"预收缩"技巧——日常防漏利器</h2>
<p>"预收缩"（The Knack）是在可能导致漏尿的动作发生前，主动快速收缩盆底肌的技巧：</p>
<ul>
<li>咳嗽或打喷嚏前瞬间，迅速有力地收缩盆底肌</li>
<li>提起重物前，预先准备盆底肌</li>
<li>从坐姿站起时，配合盆底肌收缩</li>
</ul>
<p>这个技巧需要反复练习，直到形成条件反射。</p>

<h2>何时考虑手术治疗</h2>
<p>如果经过6个月以上的规范非手术治疗仍无明显改善，且漏尿严重影响生活质量，可咨询泌尿科或妇产科医生讨论手术选择。目前主流的尿道中段悬吊术具有微创、恢复快、成功率高的特点。</p>

<p>记住：压力性尿失禁是可以改善的。最重要的是迈出第一步，开始行动。</p>`,
    category: 'bladder-health',
    imageUrl: 'https://readdy.ai/api/search-image?query=Soft%20abstract%20wellness%20and%20body%20care%20illustration%20with%20flowing%20gentle%20curves%2C%20warm%20cream%20and%20golden%20tones%2C%20feminine%20healing%20aesthetic%2C%20minimalist%20style%2C%20calm%20and%20supportive%20vibe%2C%20no%20text&width=800&height=500&seq=article-stress-incontinence&orientation=landscape',
    author: '王康复师',
    publishDate: '2026-08-04',
    readTime: '10分钟',
    tags: ['压力性尿失禁', '盆底康复', '女性健康'],
  },
];

export function getArticlesByCategory(categorySlug: string): Article[] {
  return articles.filter((article) => article.category === categorySlug);
}

export function getArticleBySlug(slug: string): Article | undefined {
  return articles.find((article) => article.slug === slug);
}

export function getCategoryName(slug: string): string {
  const categoryMap: Record<string, string> = {
    'bladder-health': '膀胱健康',
    'training-methods': '训练方法',
    'diet-nutrition': '饮食调理',
    'daily-care': '日常保健',
  };
  return categoryMap[slug] || slug;
}